import { Link, generatePath } from "react-router-dom";

import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Pagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { RemoveCircleOutline } from "@mui/icons-material";

import nameof from "ts-nameof.macro";
import { dateToStringShort } from "../../../common/utils/dateTime";
import { formatNotaryData, truncate } from "../../../common/utils/formatters";

import AlertExtended from "../../../common/components/mui-extended/AlertExtended";
import CheckboxExtended from "../../../common/components/mui-extended/CheckboxExtended";
import DatePickerExtended from "../../../common/components/mui-extended/DatePickerExtended";
import SectionExtended from "../../../common/components/mui-extended/SectionExtended";
import SelectExtended from "../../../common/components/mui-extended/SelectExtended";
import TextFieldExtended from "../../../common/components/mui-extended/TextFieldExtended";
import { Rules } from "../../../common/validationRules";
import certificationService from "../../../services/certificationService";
import notaryService from "../../../services/notaryService";
import {
  CertificationProps,
  CertificationSearchDto,
  CertificationSearchResultDto,
} from "../../../interfaces/Certification";
import { PageDefaults } from "../../../common/interfaces/PageDto";
import { useAxiosLoader } from "../../../common/hooks/useAxiosLoader";
import { useSearchForm } from "../../../common/hooks/useSearchForm";
import _ from "lodash";
import useUserContext from "../../../hooks/useUserContext";
import TableSkeleton from "../../../common/components/TableSkeleton";
import { getRoutePath } from "./utils";
import { UserType } from "../../../common/interfaces/enums/UserType";

export default function Search({ registerType }: CertificationProps) {
  const loading = useAxiosLoader(false);
  const { isOwner, userType } = useUserContext();
  const routePath = getRoutePath(registerType);

  const defaultModel: CertificationSearchDto = {
    ...PageDefaults,
    registerType: registerType,
    participants: true,
    statement: true,
  };

  const form = useSearchForm<CertificationSearchDto, CertificationSearchResultDto>({
    defaultModel: defaultModel,
    rootSearchPath: routePath.Search.Path,
    rawQueryStirngNames: [nameof(defaultModel.keywords)],
    searchAsync: async (query: string) => {
      await certificationService.getCertificationsAsync(query).then(async ({ data }) => {
        await certificationService.getCertificationsCountAsync(query).then(({ data: count }) => {
          form.setResults(data, count);
        });
      });
    },
  });

  const renderForm = () => {
    return (
      <form onReset={form.onReset} onSubmit={form.onSubmit}>
        <Stack spacing={3}>
          <SectionExtended title="Данни за удостоверяване">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SelectExtended
                  label="Нотариус"
                  name={nameof(defaultModel.notaryId)}
                  form={form}
                  searchRequest={notaryService.getNotaries}
                  initRequest={notaryService.getNotariesByIds}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextFieldExtended
                  label="Регистрационен номер"
                  name={nameof(defaultModel.registrationNumber)}
                  form={form}
                  rules={Rules.Number}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePickerExtended label="Дата" name={nameof(defaultModel.issueDate)} form={form} rules={Rules.Date} />
              </Grid>
              <Grid item xs={12} md={12}>
                <TextFieldExtended
                  label="Ключова дума/и"
                  name={nameof(defaultModel.keywords)}
                  form={form}
                  disabled={
                    !form.watch(nameof(defaultModel.participants)) && !form.watch(nameof(defaultModel.statement))
                  }
                  rules={Rules.MinLength}
                />
              </Grid>
              <Grid item>
                <Grid container spacing={3}>
                  <Grid item>
                    <CheckboxExtended label="Участници" name={nameof(defaultModel.participants)} form={form} />
                  </Grid>
                  <Grid item>
                    <CheckboxExtended label="Извлечение" name={nameof(defaultModel.statement)} form={form} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </SectionExtended>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Button startIcon={<RemoveCircleOutline />} type="reset">
                Изчисти
              </Button>
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={1}>
                {(userType === UserType.Notary || userType === UserType.NotaryDeputy) && (
                  <Button component={Link} to={routePath.New.Path} startIcon={routePath.New.Icon}>
                    {routePath.New.Label}
                  </Button>
                )}
                <LoadingButton
                  variant="contained"
                  loading={loading}
                  loadingPosition="start"
                  startIcon={routePath.Search.Icon}
                  type="submit"
                >
                  {routePath.Search.Label}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </form>
    );
  };

  const renderTable = () => {
    return loading ? (
      <TableSkeleton />
    ) : form.results && form.results.length > 0 ? (
      <>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Рег. №</TableCell>
                <TableCell>Дата</TableCell>
                <TableCell>Нотариус</TableCell>
                <TableCell>Участници</TableCell>
                <TableCell>Извлечение</TableCell>
                <TableCell width="90px"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {form.results.map((item) => (
                <TableRow key={item.certificationId} hover>
                  <TableCell>
                    {item.registrationNumberSignature && item.registrationNumberContent
                      ? item.issueDateSignature?.getTime() === item.issueDateContent?.getTime()
                        ? item.registrationNumberSignature && (
                            <>
                              {item.registrationNumberSignature}
                              <span>, </span>
                            </>
                          )
                        : item.registrationNumberSignature && (
                            <>
                              {item.registrationNumberSignature}
                              <br />
                            </>
                          )
                      : item.registrationNumberSignature}
                    {item.registrationNumberContent}
                  </TableCell>
                  <TableCell>
                    {dateToStringShort(item.issueDateSignature)}
                    {item.issueDateSignature && item.issueDateContent && <br />}
                    {item.issueDateSignature?.getTime() !== item.issueDateContent?.getTime() &&
                      dateToStringShort(item.issueDateContent)}
                  </TableCell>
                  <TableCell>{formatNotaryData(item.notary?.name, item.notary?.number)}</TableCell>
                  <TableCell>{truncate(item.participants)}</TableCell>
                  <TableCell>{truncate(item.statement)}</TableCell>
                  <TableCell>
                    <Stack direction="row" justifyContent="end">
                      {isOwner(item.ownerUserIds) && (
                        <Tooltip title={routePath.Edit.Label}>
                          <IconButton
                            component={Link}
                            to={generatePath(routePath.Edit.Path, { id: item.certificationId.toString() })}
                          >
                            {routePath.Edit.Icon}
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={routePath.Preview.Label}>
                        <IconButton
                          component={Link}
                          to={generatePath(routePath.Preview.Path, {
                            id: item.certificationId.toString(),
                          })}
                        >
                          {routePath.Preview.Icon}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={form.pageCount}
          onChange={form.onPageChanged}
          page={form.watch(nameof(defaultModel.currentPage))}
        />
      </>
    ) : (
      form.searched && <AlertExtended type="warning">Няма намерени резултати.</AlertExtended>
    );
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Регистър {_.lowerFirst(routePath.Root.Label)}</Typography>
      {renderForm()}
      {renderTable()}
    </Stack>
  );
}
